import axios from 'axios'
import { debounce, merge } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, nextTick, ref, watch } from 'vue'
import { useTransactionComparisonMasterDetails } from '@/composables/useTransactionComparisonMasterDetails'
import { useTransactionStore } from '@/stores/transaction'
import { useDetailStore } from '@/stores/useDetailStore'
import { residualAdjustment, weightsToPercentages } from '@/services/utilities'
import { overlayImputedFields } from '@/services/imputedFieldOverlay'

const cloneSimilarityConfig = config => ({
  embedWeights: [...config.embedWeights],
  zdistWeight: config.zdistWeight,
  priceDiffWeight: config.priceDiffWeight,
  rikastatusWeight: config.rikastatusWeight,
})

export const useEvaluationStore = defineStore('evaluation', () => {
  const api = axios.create()

  const id = ref(null)
  const lastSavedAt = ref(null)
  const prediction = ref({})
  const status = ref(null)
  const modelType = ref(null) // Resolved to the pinned default EBM model (useModelResolution)
  const similarityPriceModel = ref(null) // Resolved to the pinned default CatBoost model
  // RED-279: an explicit in-session pick is the only thing that overrides the
  // pinned default model on predict — set by the ModelSelector / settings popover.
  const modelTypePickedByUser = ref(false)
  const similarityPriceModelPickedByUser = ref(false)
  const aiRunning = ref(false)
  // RED-262: recompute of AI adjustments for an EXISTING kohandustabel (comparable
  // pivot edits). Kept separate from `aiRunning` (the initial "find similar" predict)
  // so the table stays mounted and updates in place instead of flashing the full loader.
  const recomputingAdjustments = ref(false)
  const buildingPart = ref(null)
  const adsOid = ref(null)
  const objectType = ref(null)
  // RED-267: land-parcel evaluation (maatükk). Seeded from the dedicated
  // `isLandParcel` Inertia prop (Evaluation::isLandParcel()) in Detail.vue —
  // NOT derivable from `objectType`, which carries the ADS address kind
  // (adob_liik), and never from empty EHR data (permit-stage buildings ride
  // on unbuilt parcels).
  const isLandParcel = ref(false)
  const recencyFilter = ref(12)
  // Legacy baseline (UI scale) — used only until the calibrated defaults from the
  // `similarityDefaults` page prop are applied via applySimilarityDefaults().
  const FALLBACK_SIMILARITY_CONFIG = {
    embedWeights: [45, 61, 47, 51],
    zdistWeight: 52,
    priceDiffWeight: 1.4,
    rikastatusWeight: 3.0,
  }
  const similarityConfig = ref(cloneSimilarityConfig(FALLBACK_SIMILARITY_CONFIG))
  // UI-scale snapshot of the calibrated defaults; resetWeights() returns to this.
  const similarityDefaultsSeed = ref(null)
  // Saved similarity_config_defaults rows (active + historical) from the
  // `similarityProfiles` page prop — the popover's profile selector. Session-scoped
  // like the rest of the RED-279 controls.
  const similarityProfiles = ref([])
  const selectedSimilarityProfileId = ref(null)
  // An explicit profile pick or a hand-dragged slider blocks auto re-resolution
  // on model change — RED-279 stickiness: the user's in-session choice wins.
  const similarityProfilePickedByUser = ref(false)
  const weightsTouchedByUser = ref(false)

  // Track IDs of comparables that have been replaced, to avoid cycling back to them
  const replacedComparableIds = ref([])

  const transactionStore = useTransactionStore()
  const detailStore = useDetailStore()
  const { storeMasterData } = useTransactionComparisonMasterDetails()

  api.interceptors.response.use(
    response => {
      updateLastSavedAt()
      return response
    },
    error => {
      console.log('error', error)
      return Promise.reject(error)
    },
  )

  const isDirty = ref(false)

  let _pendingPayload = {}

  const _debouncedPost = debounce(() => {
    const payload = _pendingPayload
    _pendingPayload = {}
    return api.post(route('evaluation.save', id.value), payload)
      .then(() => { isDirty.value = false })
      .catch(() => { /* keep dirty on error — data wasn't saved */ })
  }, 500)

  const save = (payload) => {
    isDirty.value = true
    _pendingPayload = merge(_pendingPayload, payload)
    _debouncedPost()
  }

  // RED-262: flush the debounced save and resolve once the POST completes.
  // Callers that trigger a server-side recompute (which re-reads the
  // evaluation row, e.g. the kohandustabel master pivot edit) must await
  // this so the recompute sees the just-edited values instead of racing
  // the 500ms debounce window.
  const flushSave = async () => {
    await _debouncedPost.flush()
  }

  const updateAddress = async adsOid => {
    return await api.put(route('evaluation.updateAddress', id.value), {
      adsOid,
    })
  }

  /**
   * Tear down the state that belongs to ONE evaluation, so opening another one
   * lands in the state a cold page load would.
   *
   * Pinia lives for the app, not the page: an Inertia visit from
   * /evaluation/13 to /evaluation/14 remounts Detail.vue on top of this store,
   * so without an explicit teardown evaluation 13's prediction, building part
   * and replaced-comparable list stay live under evaluation 14's id.
   *
   * Deliberately NOT cleared — modelType, similarityPriceModel, their
   * *PickedByUser flags, similarityConfig, similarityDefaultsSeed and
   * recencyFilter. RED-279 made those session-scoped on purpose: an in-session
   * model pick or slider tweak must survive navigation.
   *
   * Call this while `id` still holds the OUTGOING evaluation — the pending
   * autosave is flushed against it, otherwise the 500ms debounce fires after
   * `id` has moved on and writes the old evaluation's payload onto the new one.
   * `id` itself is left to the caller, which assigns the incoming one.
   */
  const resetForEvaluation = () => {
    _debouncedPost.flush()

    prediction.value = {}
    buildingPart.value = null
    adsOid.value = null
    objectType.value = null
    isLandParcel.value = false
    status.value = null
    lastSavedAt.value = null
    replacedComparableIds.value = []
    isDirty.value = false
    aiRunning.value = false
    recomputingAdjustments.value = false
  }

  const resetWeights = () => {
    similarityConfig.value = cloneSimilarityConfig(
      similarityDefaultsSeed.value ?? FALLBACK_SIMILARITY_CONFIG,
    )
    // Back on the seed (= the selected profile's values) — re-arm auto profile
    // resolution on model change.
    weightsTouchedByUser.value = false
  }

  /** API-scale profile row → the UI-scale shape similarityConfig holds (×100 embed/zdist). */
  const profileUiConfig = profile => ({
    embedWeights: (profile.embed_weights ?? []).map(w => w * 100),
    zdistWeight: (profile.zdist_weight ?? 0) * 100,
    priceDiffWeight: profile.price_diff_weight ?? 0,
    rikastatusWeight: profile.rikastatus_weight ?? 0,
  })

  /**
   * Client-side mirror of SimilarityConfigDefault::activeOverrides(): the active
   * row calibrated for the given model, else the active global row, else null.
   */
  const resolveProfileFor = type => {
    const rows = similarityProfiles.value
    return (
      rows.find(p => p.is_active && type && p.model_type === type) ??
      rows.find(p => p.is_active && p.scope === 'global') ??
      null
    )
  }

  /**
   * Load a saved profile into the live controls: weights (UI scale), the anchor
   * model when the profile pins one, and the reset seed — so "Taasta
   * vaikeväärtused" returns to the chosen profile, not the boot-time default.
   */
  const applySimilarityProfile = (profile, { pickedByUser = true } = {}) => {
    if (!profile) {
      return
    }

    const uiConfig = profileUiConfig(profile)
    similarityDefaultsSeed.value = cloneSimilarityConfig(uiConfig)
    similarityConfig.value = cloneSimilarityConfig(uiConfig)
    selectedSimilarityProfileId.value = profile.id
    weightsTouchedByUser.value = false

    if (pickedByUser) {
      similarityProfilePickedByUser.value = true
    }

    // An auto-resolve must never clobber an explicit in-session anchor pick.
    if (profile.similarity_price_model && (pickedByUser || !similarityPriceModelPickedByUser.value)) {
      similarityPriceModel.value = profile.similarity_price_model
      if (pickedByUser) {
        similarityPriceModelPickedByUser.value = true
      }
    }
  }

  /**
   * Point the dropdown at the resolved active profile for the current model.
   * No-op once the user has picked a profile or touched a slider this session.
   */
  const syncProfileSelection = () => {
    if (similarityProfilePickedByUser.value || weightsTouchedByUser.value) {
      return
    }
    const resolved = resolveProfileFor(modelType.value)
    if (resolved && resolved.id !== selectedSimilarityProfileId.value) {
      applySimilarityProfile(resolved, { pickedByUser: false })
    }
  }

  const setSimilarityProfiles = profiles => {
    similarityProfiles.value = Array.isArray(profiles) ? profiles : []
    syncProfileSelection()
  }

  // Switching the EBM model re-resolves the per-model calibrated profile — the
  // backend has always resolved per-model configs, but the sliders never followed.
  watch(modelType, syncProfileSelection)

  const selectedSimilarityProfile = computed(
    () => similarityProfiles.value.find(p => p.id === selectedSimilarityProfileId.value) ?? null,
  )

  // Sliders drifted from the selected profile's stored values (epsilon absorbs
  // the ×100 UI-scale round-trip).
  const profileDirty = computed(() => {
    const profile = selectedSimilarityProfile.value
    if (!profile) {
      return false
    }
    const ui = profileUiConfig(profile)
    const current = similarityConfig.value
    const close = (a, b) => Math.abs((a ?? 0) - (b ?? 0)) < 0.005
    return !(
      ui.embedWeights.length === current.embedWeights.length &&
      ui.embedWeights.every((w, i) => close(w, current.embedWeights[i])) &&
      close(ui.zdistWeight, current.zdistWeight) &&
      close(ui.priceDiffWeight, current.priceDiffWeight) &&
      close(ui.rikastatusWeight, current.rikastatusWeight)
    )
  })

  const markWeightsTouched = () => {
    weightsTouchedByUser.value = true
  }

  /**
   * RED-279: seed the expert similarity controls from the backend's effective
   * calibrated defaults (`similarityDefaults` page prop, API scale). Applied
   * once per session so in-session slider tweaks survive navigation — same
   * stickiness as an explicit model pick.
   */
  const applySimilarityDefaults = defaults => {
    if (!defaults || similarityDefaultsSeed.value) {
      return
    }

    similarityDefaultsSeed.value = {
      embedWeights: (defaults.embed_weights ?? FALLBACK_SIMILARITY_CONFIG.embedWeights.map(w => w / 100)).map(w => w * 100),
      zdistWeight: (defaults.zdist_weight ?? FALLBACK_SIMILARITY_CONFIG.zdistWeight / 100) * 100,
      priceDiffWeight: defaults.price_diff_weight ?? FALLBACK_SIMILARITY_CONFIG.priceDiffWeight,
      rikastatusWeight: defaults.rikastatus_weight ?? FALLBACK_SIMILARITY_CONFIG.rikastatusWeight,
    }
    similarityConfig.value = cloneSimilarityConfig(similarityDefaultsSeed.value)

    if (defaults.recent_months) {
      recencyFilter.value = defaults.recent_months
    }
  }

  /**
   * Add a comparable ID to the replaced list to prevent it from being suggested again.
   * Also persists to comparison_table_state.
   */
  const addReplacedComparableId = replacedId => {
    if (!replacedId) return

    const numericId = Number(replacedId)
    if (!replacedComparableIds.value.includes(numericId)) {
      replacedComparableIds.value.push(numericId)
      // Persist to comparison_table_state
      save({
        comparison_table_state: {
          replacedComparableIds: replacedComparableIds.value,
        },
      })
    }
  }

  /**
   * Initialize replaced comparable IDs from persisted state.
   */
  const initReplacedComparableIds = (ids = []) => {
    replacedComparableIds.value = Array.isArray(ids)
      ? ids.map(id => Number(id))
      : []
  }

  /**
   * Clear all replaced comparable IDs (e.g., when starting fresh prediction).
   */
  const clearReplacedComparableIds = () => {
    replacedComparableIds.value = []
    save({
      comparison_table_state: {
        replacedComparableIds: [],
      },
    })
  }

  /**
   * Initialize store from a saved prediction (loaded from database).
   * This restores the exact state from when the prediction was generated.
   * @param {Object} savedPrediction - The saved prediction data
   * @param {Array} marked - Optional marked transactions with manual changes to apply on top
   */
  const initFromSavedPrediction = async (savedPrediction, marked = []) => {
    if (!savedPrediction || !savedPrediction.available) {
      return false
    }

    // Restore prediction data
    prediction.value = savedPrediction

    // RED-279: saved `settings` are deliberately NOT restored into the live
    // controls (modelType, similarityConfig, recencyFilter). Restoring them
    // perpetuated stale state on every reload — a non-default model and the
    // redata-api server defaults (recent_months=4, zdist=1) kept re-predicting
    // forever. Live controls now always start from the pinned/calibrated
    // defaults; the saved payload itself still renders as-is.

    // Sync similar properties to transaction selection table (using same pattern as updatePrediction)
    if (savedPrediction.similar_properties?.length) {
      const predictedIds = savedPrediction.similar_properties.map(
        tr => tr.property_id,
      )

      const rows =
        await transactionStore.ensureTransactionIsInSelectionTable(predictedIds)

      const weightPercentages = weightsToPercentages(
        savedPrediction.weights,
        predictedIds,
      )

      // RED-262: per-comparable ML display values (pivot_values) by id, for
      // filling condition cells whose local pivot data is blank.
      const similarById = new Map(
        savedPrediction.similar_properties.map(p => [p.property_id, p]),
      )

      const updatedRows = rows.map(row => {
        // Support both new format (grouped_differences with technical keys)
        // and legacy format (differences.grouped with Estonian labels)
        const differences = savedPrediction.grouped_differences?.[row.id] || {}
        const unrounded =
          savedPrediction.grouped_differences_unrounded?.[row.id] || {}

        // Build _raw fields from unrounded values (AI-predicted raw adjustments)
        // New format: keys already have _raw suffix (e.g., asukoha_kohandus_raw)
        // Legacy format: keys don't have _raw suffix, so we add it
        const rawFields = {}
        Object.keys(unrounded).forEach(key => {
          if (key.endsWith('_raw')) {
            // New format: key already has _raw suffix, use as-is
            rawFields[key] = unrounded[key]
          } else {
            // Legacy format: add _raw suffix
            rawFields[`${key}_raw`] = unrounded[key]
          }
        })

        return overlayImputedFields(
          {
            ...row,
            ...differences,
            ...rawFields,
            // Leftover ML "Unknown" bucket (legacy rows). 0 for clean rows.
            maaramata_kohandus: residualAdjustment(savedPrediction, row.id),
            kaalud: weightPercentages[String(row.id)] ?? 0,
          },
          similarById.get(row.id),
        )
      })

      updatedRows.forEach(updatedRow => {
        const index = transactionStore.selectionTableRows.findIndex(
          r => r.id === updatedRow.id,
        )
        if (index !== -1) {
          transactionStore.selectionTableRows[index] = updatedRow
        }
      })

      // Apply manual changes from marked transactions (e.g., custom kaalud values)
      // These override the prediction values since they represent user edits
      if (marked?.length) {
        marked.forEach(markedTx => {
          if (markedTx.changes) {
            const targetRow = transactionStore.selectionTableRows.find(
              row => row.id === markedTx.id,
            )
            if (targetRow) {
              Object.entries(markedTx.changes).forEach(([field, value]) => {
                // RED-297: capture the pre-override value so the edited
                // marker + restore-to-original work after reload.
                transactionStore.markFieldOverridden(
                  markedTx.id,
                  field,
                  targetRow[field],
                )
                targetRow[field] = value
              })
            }
          }
        })
      }

      await nextTick()

      transactionStore.markedTransactions = predictedIds
    }

    return true
  }

  const selectedBuildingPart = computed({
    get() {
      if (buildingPart.value) {
        return buildingPart.value
      }

      if (!adsOid.value) {
        return null
      }

      const livingRoom = detailStore.ehrLivingRoom.find(
        bp => bp['adsOid'] === adsOid.value,
      )

      if (livingRoom) {
        return livingRoom
      }

      const notLivingRoom = detailStore.ehrNotLivingRoom.find(
        bp => bp['adsOid'] === adsOid.value,
      )

      if (notLivingRoom) {
        return notLivingRoom
      }

      if (
        detailStore.ehrLivingRoom.length === 1 &&
        detailStore.ehrNotLivingRoom.length === 0
      ) {
        return detailStore.ehrLivingRoom[0]
      }

      return null
    },
    set(value) {
      if (!value) {
        return
      }

      const previousAdsOid = adsOid.value
      buildingPart.value = value
      updateAddress(value?.adsOid)
      // RED-313: hand-edited subject cells describe the previous part —
      // a newly chosen part shows its own values.
      if (previousAdsOid && previousAdsOid !== value?.adsOid) {
        transactionStore.clearMasterOverrides()
      }
      storeMasterData()

      if (previousAdsOid && previousAdsOid !== value?.adsOid) {
        transactionStore.resetComparableState()
      }
    },
  })

  const updateSelectedTransactions = async transaction => {
    return new Promise((resolve, reject) => {
      return api
        .put(`/api/evaluation/${id.value}/selected`, transaction)
        .then(() => {
          resolve(transaction)
        })
        .catch(err => {
          console.log(err)
          reject(err.toString())
        })
    })
  }

  const getSimilarTransactionById = computed(() => {
    return id => {
      if (!prediction.value.similar_properties) {
        return null
      }
      return (
        prediction.value.similar_properties.find(
          tr => tr.property_id === Number.parseInt(id),
        ) || null
      )
    }
  })

  const updateMarkedTransactions = async (markedTransactions = []) => {
    return new Promise((resolve, reject) => {
      return api
        .put(`/api/evaluation/${id.value}/marked`, {
          marked_transactions: markedTransactions.map(id => Number(id)),
        })
        .then(() => {
          resolve()
        })
        .catch(err => {
          console.log(err)
          reject(err.toString())
        })
    })
  }

  // Shared by predict() and the comparable-replace flow (ComparisonTable) so
  // both requests carry the same similarity overrides — a replace must draw
  // from the same pool as the predict that produced the table.
  const buildSimilarityOverridesPayload = () => {
    const payload = {}

    // Normalize weights from 0-100 scale to API expected scale (around 1.0)
    const normalizedEmbedWeights = similarityConfig.value.embedWeights?.map(
      w => w / 100,
    )
    const normalizedZdistWeight =
      similarityConfig.value.zdistWeight !== null
        ? similarityConfig.value.zdistWeight / 100
        : null
    const priceDiffWeight = similarityConfig.value.priceDiffWeight ?? null
    const rikastatusWeight = similarityConfig.value.rikastatusWeight ?? null

    // Only include weights if they're provided
    if (normalizedEmbedWeights) {
      payload.embed_weights = normalizedEmbedWeights
    }
    if (normalizedZdistWeight !== null) {
      payload.zdist_weight = normalizedZdistWeight
    }
    if (priceDiffWeight !== null) {
      payload.price_diff_weight = priceDiffWeight
    }
    if (rikastatusWeight !== null) {
      payload.rikastatus_weight = rikastatusWeight
    }
    if (similarityPriceModel.value) {
      payload.similarity_price_model = similarityPriceModel.value
    }

    return payload
  }

  const predict = async () => {
    aiRunning.value = true
    await transactionStore.resetComparableState()
    await updateMarkedTransactions()

    const payload = {
      model_type: modelType.value,
      evaluation_id: id.value,
      building_part_id: selectedBuildingPart.value['ehitise-osa-number'],
      recent_months: recencyFilter.value,
      ...buildSimilarityOverridesPayload(),
    }

    return new Promise((resolve, reject) => {
      return api
        .post('/api/building-part/evaluation', payload)
        .then(response => {
          aiRunning.value = false
          resolve(response.data)
        })
        .catch(err => {
          aiRunning.value = false
          console.log(err)
          reject(err.toString())
        })
    })
  }

  const updatePrediction = async (p, { preserveMarkedTransactions = false } = {}) => {
    try {
      prediction.value = p

      const predictedIds = p.similar_properties?.map(tr => tr.property_id) || []
      if (predictedIds.length === 0) {
        return
      }

      const rows =
        await transactionStore.ensureTransactionIsInSelectionTable(predictedIds)

      const weightPercentages = weightsToPercentages(p.weights, predictedIds)

      const similarById = new Map(
        (p.similar_properties || []).map(sp => [sp.property_id, sp]),
      )

      const updatedRows = rows.map(row =>
        overlayImputedFields(
          {
            ...row,
            ...p.grouped_differences?.[row.id],
            maaramata_kohandus: residualAdjustment(p, row.id),
            kaalud: weightPercentages[String(row.id)] ?? 0,
          },
          similarById.get(row.id),
        ),
      )

      updatedRows.forEach(updatedRow => {
        const index = transactionStore.selectionTableRows.findIndex(
          r => r.id === updatedRow.id,
        )
        if (index !== -1) {
          transactionStore.selectionTableRows[index] = updatedRow
        }
      })

      await nextTick()

      if (!preserveMarkedTransactions) {
        transactionStore.markedTransactions = predictedIds
      }
    } catch (error) {
      console.error('Error updating prediction:', error)
      prediction.value = {}
      throw error
    }
  }

  /**
   * Calculate adjustments and weights for manually added comparable transactions.
   *
   * @param {number[]} comparableIds - Array of transaction IDs to calculate adjustments for
   * @param {Object} [comparableFeatures] - Optional feature overrides for specific comparables
   * @returns {Promise<Object>} Adjustments and weights for the comparables
   */
  const calculateComparablesAdjustments = async (
    comparableIds,
    comparableFeatures = null,
  ) => {
    if (!comparableIds || comparableIds.length === 0) {
      console.warn('No comparable IDs provided for adjustment calculation')
      return null
    }

    recomputingAdjustments.value = true

    try {
      const payload = {
        evaluation_id: id.value,
        building_part_id: selectedBuildingPart.value['ehitise-osa-number'],
        comparable_ids: comparableIds,
        model_type: modelType.value,
      }

      // Add feature overrides if provided
      if (comparableFeatures) {
        payload.comparable_features = comparableFeatures
      }

      const response = await api.post(
        '/api/building-part/evaluation/calculate-adjustments',
        payload,
      )

      recomputingAdjustments.value = false

      if (response.data.available) {
        return response.data
      } else {
        console.error('Adjustment calculation failed:', response.data.error)
        return null
      }
    } catch (err) {
      recomputingAdjustments.value = false
      console.error('Error calculating adjustments:', err)
      return null
    }
  }

  /**
   * Update prediction data with manually calculated adjustments.
   * Now uses the same structure as updatePrediction since backend returns predict flow format.
   *
   * @param {Object} adjustmentData - Response from calculateComparablesAdjustments
   */
  const updateComparablesAdjustments = async adjustmentData => {
    try {
      // MERGE over the last full prediction, replacing only the recompute's own
      // keys — the calc response deliberately omits details_log (used by the
      // comparison table's distance column), comparable_average, display_price
      // and settings, and a whole-object replace used to wipe them on every
      // table edit. The whitelist stays explicit: calc's details_log is an
      // intentionally empty array, so spreading adjustmentData would clobber.
      prediction.value = {
        ...prediction.value,
        similar_properties: adjustmentData.similar_properties || [],
        grouped_differences: adjustmentData.grouped_differences || {},
        grouped_differences_unrounded:
          adjustmentData.grouped_differences_unrounded || {},
        weights: adjustmentData.weights || {},
        feature_groups: adjustmentData.feature_groups || {},
        top_contributions: adjustmentData.top_contributions || {},
        original_values: adjustmentData.original_values || {},
        target_property_id: adjustmentData.target_property_id,
        // Falls back to the last full prediction's copy so an older ml-api
        // (no contributions on the calc response) degrades, never blanks.
        subject_contributions:
          adjustmentData.subject_contributions ||
          prediction.value?.subject_contributions ||
          null,
      }

      const predictedIds =
        adjustmentData.similar_properties?.map(tr => tr.property_id) || []
      if (predictedIds.length === 0) {
        return
      }

      const rows =
        await transactionStore.ensureTransactionIsInSelectionTable(predictedIds)

      const weightPercentages = weightsToPercentages(
        adjustmentData.weights,
        predictedIds,
      )

      const updatedRows = rows.map(row => ({
        ...row,
        ...adjustmentData.grouped_differences?.[row.id],
        ...adjustmentData.grouped_differences_unrounded?.[row.id],
        maaramata_kohandus: residualAdjustment(adjustmentData, row.id),
        kaalud: weightPercentages[String(row.id)] ?? 0,
      }))

      updatedRows.forEach(updatedRow => {
        const index = transactionStore.selectionTableRows.findIndex(
          r => r.id === updatedRow.id,
        )
        if (index !== -1) {
          transactionStore.selectionTableRows[index] = updatedRow
        }
      })

      await nextTick()
    } catch (error) {
      console.error('Error updating manual comparables adjustments:', error)
      prediction.value = {}
      throw error
    }
  }

  const updateLastSavedAt = () => {
    lastSavedAt.value = Math.floor(Date.now())
  }

  return {
    id,
    isDirty,
    lastSavedAt,
    save,
    flushSave,
    updateAddress,
    resetForEvaluation,
    status,
    updateSelectedTransactions,
    updateMarkedTransactions,
    updatePrediction,
    prediction,
    getSimilarTransactionById,
    modelType,
    similarityPriceModel,
    modelTypePickedByUser,
    similarityPriceModelPickedByUser,
    applySimilarityDefaults,
    similarityDefaultsSeed,
    similarityProfiles,
    setSimilarityProfiles,
    selectedSimilarityProfileId,
    selectedSimilarityProfile,
    similarityProfilePickedByUser,
    applySimilarityProfile,
    resolveProfileFor,
    profileDirty,
    weightsTouchedByUser,
    markWeightsTouched,
    predict,
    buildSimilarityOverridesPayload,
    selectedBuildingPart,
    adsOid,
    buildingPart,
    aiRunning,
    recomputingAdjustments,
    recencyFilter,
    similarityConfig,
    resetWeights,
    calculateComparablesAdjustments,
    updateComparablesAdjustments,
    replacedComparableIds,
    addReplacedComparableId,
    initReplacedComparableIds,
    clearReplacedComparableIds,
    initFromSavedPrediction,
    objectType,
    isLandParcel,
  }
})
